import { Controller } from '@hotwired/stimulus';
import setFrameSource from '@/js/util/set_frame_source';
import { initTooltips } from '@/js/modules/tooltips';

export default class extends Controller {
  static targets = ['pills', 'input', 'dropdown', 'pillTemplate', 'hiddenInputTemplate'];
  static values = { url: String, maxSelections: Number, placeholder: String };

  #selectedIds = new Set();
  #debounceTimer = null;
  #observer = null;

  connect() {
    this.pillsTarget.querySelectorAll('[data-client-id]').forEach((pill) => {
      this.#selectedIds.add(pill.dataset.clientId);
    });
    this.#updatePlaceholder();

    this.#observer = new MutationObserver(this.#handlePillsMutation);
    this.#observer.observe(this.pillsTarget, { childList: true });

    document.addEventListener('click', this.handleOutsideClick);

    if (this.#selectedIds.size > 0) {
      queueMicrotask(() => this.#dispatchChange());
    }
  }

  disconnect() {
    this.#observer?.disconnect();
    document.removeEventListener('click', this.handleOutsideClick);
  }

  handleOutsideClick = (event) => {
    if (!this.element.contains(event.target)) {
      this.hideDropdown();
    }
  };

  search() {
    const query = this.inputTarget.value.trim();

    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => {
      this.#updateFrame(query);
    }, 250);
  }

  focusInput(event) {
    if (event.target.closest('[data-client-id], a, button')) {
      return;
    }
    if (this.dropdownTarget.contains(event.target)) {
      return;
    }

    this.inputTarget.focus();
  }

  showDropdown() {
    this.dropdownTarget.classList.remove('hidden');
    this.#updateFrame(this.inputTarget.value.trim());
  }

  hideDropdown() {
    this.dropdownTarget.classList.add('hidden');
  }

  select(event) {
    const { id, name, email } = event.params;
    const clientId = String(id);

    if (this.#selectedIds.has(clientId)) {
      return;
    }

    if (this.maxSelectionsValue > 0 && this.#selectedIds.size >= this.maxSelectionsValue) {
      this.#clearSelections();
    }

    this.#selectedIds.add(clientId);
    this.#addPill({ id: clientId, name, email });
    this.#addHiddenInput(clientId);
    this.inputTarget.value = '';
    this.hideDropdown();
    this.#dispatchChange();
  }

  remove(event) {
    const { id } = event.params;
    const clientId = String(id);

    this.#selectedIds.delete(clientId);

    const pill = this.pillsTarget.querySelector(`[data-client-id="${clientId}"]`);
    if (pill) {
      pill.remove();
    }

    const input = this.element.querySelector(`input[type="hidden"][value="${clientId}"]`);
    if (input) {
      input.remove();
    }

    this.#updatePlaceholder();
    this.#dispatchChange();
  }

  #updatePlaceholder() {
    this.inputTarget.placeholder = this.#selectedIds.size === 0 ? this.placeholderValue : '';
  }

  #updateFrame(query) {
    const frame = this.dropdownTarget.querySelector('turbo-frame');
    if (frame) {
      setFrameSource(frame, this.urlValue, {
        search: query,
        selected: [...this.#selectedIds].join(','),
      });
    }
  }

  #addPill({ id, name, email }) {
    const label = name || email;
    const pill = this.pillTemplateTarget.content.firstElementChild.cloneNode(true);
    pill.dataset.clientId = id;
    pill.dataset.clientEmail = email || '';
    pill.querySelector('[data-role="label"]').textContent = label;
    pill.querySelector('button').setAttribute('data-clients--client-selector-id-param', id);

    const tooltipContent = pill.querySelector('[data-role="tooltip"]');
    if (tooltipContent) {
      tooltipContent.textContent = `${name} (${email})`;
    }

    this.inputTarget.before(pill);
    initTooltips(pill);
  }

  #addHiddenInput(id) {
    const input = this.hiddenInputTemplateTarget.content.firstElementChild.cloneNode(true);
    input.value = id;
    this.inputTarget.before(input);
  }

  // select() and remove() update #selectedIds and dispatch themselves, so only pills added
  // or removed from outside (e.g. the new client modal) change the selection here.
  #handlePillsMutation = (mutations) => {
    const previousIds = new Set(this.#selectedIds);

    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE && node.dataset.clientId) {
          const newId = node.dataset.clientId;
          if (
            !this.#selectedIds.has(newId) &&
            this.maxSelectionsValue > 0 &&
            this.#selectedIds.size >= this.maxSelectionsValue
          ) {
            this.#clearSelections();
          }
          this.#selectedIds.add(newId);
        }
      }
      for (const node of mutation.removedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE && node.dataset.clientId) {
          this.#selectedIds.delete(node.dataset.clientId);
        }
      }
    }
    this.#updatePlaceholder();

    const changed =
      previousIds.size !== this.#selectedIds.size ||
      [...previousIds].some((id) => !this.#selectedIds.has(id));
    if (changed) {
      this.#dispatchChange();
    }
  };

  #clearSelections() {
    [...this.#selectedIds].forEach((existingId) => {
      this.#selectedIds.delete(existingId);
      this.pillsTarget.querySelector(`[data-client-id="${existingId}"]`)?.remove();
      this.element.querySelector(`input[type="hidden"][value="${existingId}"]`)?.remove();
    });
  }

  #dispatchChange() {
    const clients = this.#getSelectedClients();
    this.dispatch('change', { detail: { clients }, bubbles: true });
  }

  #getSelectedClients() {
    return [...this.pillsTarget.querySelectorAll('[data-client-id]')].map((pill) => ({
      id: pill.dataset.clientId,
      name: pill.querySelector('[data-role="label"]').textContent,
      email: pill.dataset.clientEmail || null,
    }));
  }
}
